import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  AlignVerticalBottom as AlignVerticalBottomIcon,
  AlignVerticalCenter as AlignVerticalCenterIcon,
  AlignVerticalTop as AlignVerticalTopIcon,
  FormatAlignCenter as FormatAlignCenterIcon,
  FormatAlignLeft as FormatAlignLeftIcon,
  FormatAlignRight as FormatAlignRightIcon,
  Wallpaper as WallpaperIcon,
} from '@mui/icons-material'
import { Box, Paper, Stack, Tooltip } from '@mui/material'
import PropTypes from 'prop-types'
import {
  ActionButton,
  HTMLEditor,
  Picto,
  RenderCount,
  Section,
  TextField,
} from '~/components'
import { DnDTypes } from '~/constants'
import { useRouteContext } from '~/routes'
import { bshSlice } from '~/store'
import { isValidBalancedHtml } from '~/utils'
import Anim from './Anim'
import Comparsion from './comparsion'
import Field from './Field'
import { useCopyViewerText, useParagraph } from './hooks'
import IconChooseDialog from './IconChooseDialog'
import Image from './Image'
import Movie from './Movie'
import ParagraphContext from './ParagraphContext'
import PropertyAssigns from './PropertyAssigns'
import RestoreFromParentDialog from './RestoreFromParentDialog'
import Skip from './Skip'
import Title from './Title'

const Paragraph = (props) => {
  const {
    draggable,
    index,
    paragraph: paragraphProp,
    paragraphType,
    reorderParagraphs,
    swapParagraphs,
    onEditing,
    onMoveDown,
    onMoveUp,
    onRemoved,
  } = props
  const { t } = useTranslation()

  const { product } = useRouteContext(Paragraph.name)

  const ref = useRef()

  const paragraphContainer = useParagraph({
    paragraph: paragraphProp,
    ref,
    onEditing,
    onMoveDown,
    onMoveUp,
    onRemoved,
  })
  const {
    canEdit,
    editable,
    paragraph,
    onPropertyChange,
    diffFromParent,
    restoreToParent,
    saveParagraph,
  } = paragraphContainer
  const copyViewerText = useCopyViewerText(canEdit)
  const viewerFieldSx = useMemo(
    () => (canEdit ? undefined : { cursor: 'pointer' }),
    [canEdit]
  )

  const [title, setTitle] = useState(paragraph.title || '')
  const [titleHtmlError, setTitleHtmlError] = useState(false)
  const [showIconChooseDialog, setShowIconChooseDialog] = useState(false)
  const [restoreDiff, setRestoreDiff] = useState(null)

  useEffect(() => {
    queueMicrotask(() => {
      setTitle(paragraph.title || '')
      setTitleHtmlError(false)
    })
  }, [paragraph.title])

  const handleRestoreClick = useCallback(async () => {
    const diff = await diffFromParent()
    if (diff) {
      setRestoreDiff(diff)
    }
  }, [diffFromParent])

  const handleRestoreConfirm = useCallback(async () => {
    await restoreToParent()
  }, [restoreToParent])

  const restorable = useMemo(() => {
    return paragraph.status?.includes('M')
  }, [paragraph.status])

  const restoreParagraphButton = useMemo(() => {
    return (
      restorable && (
        <Tooltip
          title={t('paragraph.restore-to-parent', {
            fields: t('paragraph.all-fields').toLowerCase(),
          })}
        >
          <Picto letter="M" onClick={handleRestoreClick} />
        </Tooltip>
      )
    )
  }, [handleRestoreClick, restorable, t])

  const bottom = useMemo(() => {
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={0.5}
      >
        <Skip />
        <Stack alignItems="baseline" direction="row" spacing={1}>
          {!product && <PropertyAssigns />}
          <Stack direction="row" alignItems="center" spacing={1}>
            {restoreParagraphButton}
          </Stack>
        </Stack>
      </Stack>
    )
  }, [product, restoreParagraphButton])

  const textContainsEmpCo = useSelector((state) =>
    bshSlice.containsEmpCo(state, paragraph.text)
  )

  const imageContainsEmpCo = useSelector((state) =>
    bshSlice.containsEmpCo(state, paragraph.media)
  )

  const iconCaptionContainsEmpCo = useSelector((state) =>
    bshSlice.containsEmpCo(state, paragraph.icon_caption)
  )

  const content = useMemo(() => {
    if (paragraphType !== 'comparsion') {
      if (paragraphType === 'title') {
        return bottom
      }
      if (paragraphType === 'text') {
        return (
          <>
            <Field
              name="text"
              render={(fieldProps) => (
                <Box
                  onClick={() => copyViewerText(paragraph.text)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    ...viewerFieldSx,
                  }}
                >
                  <HTMLEditor
                    disabled={!editable}
                    highlighted={textContainsEmpCo}
                    sx={{ flexGrow: 1 }}
                    text={paragraph.text}
                    {...fieldProps}
                  />
                </Box>
              )}
              submitOnEnter={false}
            />
            {bottom}
          </>
        )
      }
      const sx = (() => {
        switch (paragraphType) {
          case 'left':
          case 'movie-left':
          case 'anim-left':
            return {
              flexDirection: 'row',
              '& > .content': { width: '42%', flexShrink: 0 },
            }
          case 'icon':
          case 'function':
            return {
              flexDirection: 'row',
              '& > .content': {
                width: '20%',
                flexShrink: 0,
              },
              '& > .content img': { maxWidth: '120px' },
            }
          case 'right':
          case 'movie-right':
          case 'anim-right':
            return {
              flexDirection: 'row-reverse',
              '& > .content': { width: '42%', flexShrink: 0 },
            }
          case 'top':
            return { flexDirection: 'column' }
          case 'bottom':
          case 'movie':
          case 'anim':
            return { flexDirection: 'column-reverse' }
        }
      })()
      const hasSideIconFields = ['left', 'right'].includes(paragraphType)
      const content = (() => {
        switch (paragraphType) {
          case 'top':
          case 'bottom':
          case 'left':
          case 'right':
          case 'icon':
          case 'function':
            return (
              <Image
                highlighted={
                  paragraphType === 'icon' ? imageContainsEmpCo : undefined
                }
                movie={!['icon', 'function'].includes(paragraphType)}
                alt={{
                  hidden: ['function'].includes(paragraphType),
                  multiline: ['left', 'right'].includes(paragraphType),
                }}
              />
            )
          case 'movie':
          case 'movie-left':
          case 'movie-right':
            return <Movie />
          case 'anim':
          case 'anim-left':
          case 'anim-right':
            return <Anim />
        }
      })()
      return (
        <>
          <Stack spacing={1} sx={sx}>
            <Box className="content">{content}</Box>
            <Stack spacing={1} sx={{ flexGrow: 1 }}>
              {hasSideIconFields && (
                <Stack direction="row" spacing={1}>
                  <Box sx={{ width: '100px', flexShrink: 0 }}>
                    <Image
                      alt={{ hidden: true }}
                      placeholder={t('paragraph.icon-upload')}
                      showDelete
                      type="icon"
                    />
                  </Box>
                  <Stack
                    direction="column"
                    spacing={1}
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{ flexGrow: 1 }}
                  >
                    <Field
                      name="icon_caption"
                      render={(fieldProps) => (
                        <Box
                          onClick={() => copyViewerText(paragraph.icon_caption)}
                          sx={{ width: '100%', ...viewerFieldSx }}
                        >
                          <TextField
                            editable={editable}
                            highlighted={iconCaptionContainsEmpCo}
                            placeholder={t('paragraph.icon-caption')}
                            size="small"
                            sx={{ width: '100%' }}
                            value={paragraph.icon_caption || ''}
                            {...fieldProps}
                          />
                        </Box>
                      )}
                      sx={{ width: '100%' }}
                    />
                    <Field
                      name="icon"
                      render={() => (
                        <ActionButton
                          background
                          icon={<WallpaperIcon />}
                          size="small"
                          title={t('paragraph.icon-choose')}
                          onClick={() => setShowIconChooseDialog(true)}
                        />
                      )}
                      shift={false}
                    />
                    <IconChooseDialog
                      open={showIconChooseDialog}
                      targetParagraphId={paragraph.id}
                      onClose={() => setShowIconChooseDialog(false)}
                    />
                  </Stack>
                </Stack>
              )}
              <Field
                name="text"
                render={(fieldProps) => (
                  <Box
                    onClick={() => copyViewerText(paragraph.text)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flexGrow: 1,
                      ...viewerFieldSx,
                    }}
                  >
                    <HTMLEditor
                      disabled={!editable}
                      highlighted={textContainsEmpCo}
                      sx={{ flexGrow: 1 }}
                      text={paragraph.text}
                      {...fieldProps}
                    />
                  </Box>
                )}
                submitOnEnter={false}
              />
            </Stack>
          </Stack>
          {bottom}
        </>
      )
    } else {
      return <Comparsion bottom={bottom} />
    }
  }, [
    bottom,
    copyViewerText,
    editable,
    viewerFieldSx,
    iconCaptionContainsEmpCo,
    imageContainsEmpCo,
    paragraph.icon_caption,
    paragraph.id,
    paragraph.text,
    paragraphType,
    showIconChooseDialog,
    t,
    textContainsEmpCo,
  ])

  const [{ isDragging }, drag] = useDrag({
    type: DnDTypes.PARAGRAPH,
    item: () => {
      return { id: paragraph.id, index }
    },
    canDrag: draggable,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })
  const [{ handlerId }, drop] = useDrop({
    accept: DnDTypes.PARAGRAPH,
    collect(monitor) {
      return {
        canDrop: monitor.canDrop(),
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (
        !ref.current ||
        !monitor.canDrop() ||
        !monitor.isOver({ shallow: true })
      ) {
        return
      }
      // console.log('item', monitor.getItemType())
      const dragIndex = item.index
      const hoverIndex = index
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      // Determine mouse position
      const clientOffset = monitor.getClientOffset()
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      if (
        // Dragging downwards
        (dragIndex < hoverIndex &&
          hoverClientY < hoverBoundingRect.height * 0.5) ||
        // Dragging upwards
        (dragIndex > hoverIndex &&
          hoverClientY > hoverBoundingRect.height * 0.5)
      ) {
        return
      }
      // Time to actually perform the action
      // swapParagraphs(item.id, paragraph.id)
      swapParagraphs(item.index, hoverIndex)
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
    drop() {
      reorderParagraphs()
    },
  })

  const setParagraphSectionRef = useCallback(
    (node) => {
      ref.current = node
      drag(drop(node))
    },
    [drag, drop]
  )
  const opacity = isDragging ? 0.5 : 1

  const titleContainsEmpCo = useSelector((state) =>
    bshSlice.containsEmpCo(state, title)
  )

  return (
    <ParagraphContext.Provider
      paragraph={paragraphProp}
      value={paragraphContainer}
    >
      <Section
        ref={setParagraphSectionRef}
        data-handler-id={handlerId}
        key={paragraph.id}
        as={Paper}
        sectionId={`${Paragraph.name}-${paragraph.id}`}
        sx={{
          backgroundColor: 'background.default',
          opacity,
          p: 1,
          '> *': !canEdit || editable ? undefined : { pointerEvents: 'none' },
        }}
        // tabIndex={0}
        title={<Title restorable={restorable} />}
        // onFocus={() => setEditable(true)}
        // onBlur={(e) => setEditable(ref.current.contains(e.relatedTarget))}
      >
        <Stack spacing={1}>
          <RenderCount />
          {paragraphType !== 'text' && (
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Field
                name="title"
                render={(fieldProps) => (
                  <Box
                    onClick={() => copyViewerText(title)}
                    sx={{ flexGrow: 1, ...viewerFieldSx }}
                  >
                    <TextField
                      editable={editable}
                      error={titleHtmlError}
                      helperText={
                        titleHtmlError
                          ? t('paragraph.title-html-invalid')
                          : undefined
                      }
                      highlighted={titleContainsEmpCo}
                      placeholder={t('paragraph.title')}
                      size="small"
                      sx={{ display: 'flex' }}
                      value={title}
                      {...fieldProps}
                      onChange={(e) => {
                        const value = e.target.value
                        setTitle(value)
                        const valid = isValidBalancedHtml(value)
                        setTitleHtmlError(!valid)
                        if (valid) {
                          fieldProps.onChange?.(e)
                        }
                      }}
                    />
                  </Box>
                )}
                sx={{ flexGrow: 1 }}
              />
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {(() =>
                  [
                    { icon: FormatAlignLeftIcon, align: 'left' },
                    { icon: FormatAlignCenterIcon, align: 'center' },
                    { icon: FormatAlignRightIcon, align: 'right' },
                  ].map(({ icon: Icon, align }, index) => (
                    <ActionButton
                      key={index}
                      background
                      icon={
                        <Icon
                          sx={
                            paragraph.title_align === align
                              ? undefined
                              : {
                                  color: 'background.default',
                                }
                          }
                        />
                      }
                      size="small"
                      onClick={() => {
                        onPropertyChange('title_align', align)
                        setTimeout(() => {
                          saveParagraph({ title_align: align })
                        }, 0)
                      }}
                    />
                  )))()}
              </Stack>
              {paragraphType !== 'title' && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {(() =>
                    [
                      { icon: AlignVerticalTopIcon, valign: 'top' },
                      { icon: AlignVerticalCenterIcon, valign: 'middle' },
                      { icon: AlignVerticalBottomIcon, valign: 'bottom' },
                    ].map(({ icon: Icon, valign }, index) => (
                      <ActionButton
                        key={index}
                        background
                        icon={
                          <Icon
                            sx={
                              paragraph.title_valign === valign
                                ? undefined
                                : {
                                    color: 'background.default',
                                  }
                            }
                          />
                        }
                        size="small"
                        onClick={() => {
                          onPropertyChange('title_valign', valign)
                          setTimeout(() => {
                            saveParagraph({ title_valign: valign })
                          }, 0)
                        }}
                      />
                    )))()}
                </Stack>
              )}
            </Stack>
          )}
          {content}
        </Stack>
      </Section>
      <RestoreFromParentDialog
        diff={restoreDiff}
        open={restoreDiff !== null}
        onClose={() => setRestoreDiff(null)}
        onConfirm={handleRestoreConfirm}
      />
    </ParagraphContext.Provider>
  )
}

Paragraph.propTypes = {
  draggable: PropTypes.bool,
  index: PropTypes.number.isRequired,
  paragraph: PropTypes.object.isRequired,
  paragraphType: PropTypes.string.isRequired,
  reorderParagraphs: function (props, propName, componentName) {
    const value = props[propName]
    PropTypes.checkPropTypes(
      {
        [propName]: props.draggable
          ? PropTypes.func.isRequired
          : PropTypes.func,
      },
      { [propName]: value },
      propName,
      componentName
    )
  },
  swapParagraphs: function (props, propName, componentName) {
    const value = props[propName]
    PropTypes.checkPropTypes(
      {
        [propName]: props.draggable
          ? PropTypes.func.isRequired
          : PropTypes.func,
      },
      { [propName]: value },
      propName,
      componentName
    )
  },
  onEditing: PropTypes.func,
  onMoveDown: PropTypes.func,
  onMoveUp: PropTypes.func,
  onRemoved: PropTypes.func,
}

export default Paragraph
